/**
 * Every sentence the Strip Card surfaces show on web (ISSUE-1287 to
 * ISSUE-1298). Copy rules live in docs/features/contest-strips/overview.md,
 * "Words": the game, the card, strips, rows, spots, digits and winners,
 * nothing else. Plain, warm American English, sentence case.
 */

// The create step (ISSUE-1287).
export const STRIP_CARD_SETUP_STEP_TITLE = "Strip Card setup";
export const STRIP_SETUP_FIRST_GAME_HEADING = "First game";
export const STRIP_SETUP_FIRST_GAME_HINT =
  "Pick a day, then the game your first card is for. You can add more games later.";
export const STRIP_SETUP_NO_LEAGUE =
  "Pick a league first, then come back to choose the game.";
// ISSUE-1299. A pool with no league names its first game instead.
export const STRIP_SETUP_CUSTOM_FIRST_GAME_HINT =
  "Name the game your first card is for. You can add more games later, and an admin picks the winners by hand.";
export const STRIP_SETUP_CUSTOM_FIRST_GAME_DEFAULT = "Custom game 1";

// The game picker, shared by the create step and Add a game.
export const STRIP_PICKER_DAY_LABEL = "Game day";
export const STRIP_PICKER_DAY_PLACEHOLDER = "Choose a day";
export const STRIP_PICKER_GAME_LABEL = "Game";
export const STRIP_PICKER_NO_DAYS =
  "No upcoming games for this league in the next few weeks.";
export const STRIP_PICKER_NO_GAMES =
  "Every game on this day has started. Pick another day.";
export const STRIP_PICKER_LOADING = "Loading games";

// Max spots per member (create step and Settings).
export const STRIP_MAX_SPOTS_LABEL = "Spots per member on each strip";
export const STRIP_MAX_SPOTS_UNLIMITED = "Unlimited";
export function stripMaxSpotsChoiceLabel(value: number | null): string {
  if (value === null) return STRIP_MAX_SPOTS_UNLIMITED;
  return value === 1 ? "1 spot" : `${value} spots`;
}
export function stripMaxSpotsSummary(value: number | null): string {
  if (value === null) {
    return "Each member can hold as many rows as they like on a strip.";
  }
  return value === 1
    ? "Each member can hold 1 row on a strip."
    : `Each member can hold up to ${value} rows on a strip.`;
}

// The Game tab: frame, switcher and header (ISSUE-1288, ISSUE-1292).
export const STRIP_GAME_SWITCHER_LABEL = "Game";
export const STRIP_ADD_GAME_LABEL = "Add a game";
export const STRIP_REMOVE_GAME_LABEL = "Remove game";
export const STRIP_GAME_CANCELED_TAG = "Canceled";
export const STRIP_GAME_LOAD_FAILED =
  "We couldn't load this card. Try again in a moment.";
export const STRIP_TRY_AGAIN_LABEL = "Try again";
export const STRIP_NO_GAMES =
  "This pool has no games yet. An admin can add one.";
export const STRIP_CANCELED_BANNER =
  "This game won't be played. The card is read only, and its winners were cleared.";

// The board (ISSUE-1288).
export const STRIP_BOARD_LABEL = "Strip card";
export const STRIP_USERS_COLUMN_LABEL = "Members";
export const STRIP_OPEN_ROW_LABEL = "Open";
export const STRIP_TAKE_ROW_LABEL = "Take";
export const STRIP_YOU_LABEL = "You";
export const STRIP_STATE_OPEN = "Open";
export const STRIP_STATE_LOCKED = "Locked";
export const STRIP_ADD_STRIP_LABEL = "Add a strip";
export const STRIP_MANAGE_LABEL = "Manage";
export function stripManageAriaLabel(stripLabel: string): string {
  return `Manage ${stripLabel}`;
}
export function stripSelectAriaLabel(stripLabel: string): string {
  return `Show ${stripLabel}`;
}
export function stripRowAriaLabel(input: {
  rowLabel: string;
  stripLabel: string;
  holder: string | null;
}): string {
  const who = input.holder === null ? "open" : `held by ${input.holder}`;
  return `${input.stripLabel}, row ${input.rowLabel}, ${who}`;
}
export function stripDigitAriaLabel(input: {
  rowLabel: string;
  stripLabel: string;
  digit: number | null;
  winnerCount: number;
}): string {
  const digit =
    input.digit === null ? "no digit yet" : `digit ${String(input.digit)}`;
  const winners =
    input.winnerCount === 0
      ? ""
      : input.winnerCount === 1
        ? ", 1 winner"
        : `, ${String(input.winnerCount)} winners`;
  return `${input.stripLabel}, row ${input.rowLabel}, ${digit}${winners}`;
}
export function stripFilledSummary(input: {
  filledCount: number;
  rowCount: number;
  mySpotCount: number;
}): string {
  const mine =
    input.mySpotCount === 0
      ? "You have no rows yet."
      : input.mySpotCount === 1
        ? "You have 1 row."
        : `You have ${String(input.mySpotCount)} rows.`;
  return `${String(input.filledCount)} of ${String(input.rowCount)} rows taken. ${mine}`;
}

// Notes for a tap the server already said no to (shown inline, closable).
export const STRIP_NOTE_LIMIT_REACHED =
  "You hold the most rows allowed on this strip. Give one back to take another.";
export const STRIP_NOTE_STRIP_LOCKED =
  "This strip is locked, so its rows can't change.";
export const STRIP_NOTE_GAME_CANCELED =
  "This game won't be played, so the card can't change.";
export const STRIP_NOTE_SOMEONE_ELSE =
  "Another member holds this row. Pick an open one.";

// The member picker for an admin (ISSUE-1288).
export function stripAssignTitle(input: {
  stripLabel: string;
  rowLabel: string;
}): string {
  return `${input.stripLabel}, row ${input.rowLabel}`;
}
export const STRIP_ASSIGN_LIST_LABEL = "Who holds this row";
export const STRIP_ASSIGN_NOBODY = "Nobody";
export const STRIP_ASSIGN_NOBODY_HINT = "Leave the row open.";
export function stripAssignMemberHint(input: {
  spotCount: number;
  isAtLimit: boolean;
}): string {
  const rows =
    input.spotCount === 1
      ? "1 row on this strip"
      : `${String(input.spotCount)} rows on this strip`;
  return input.isAtLimit ? `${rows}, at the limit` : rows;
}
export const STRIP_ASSIGN_SAVE_LABEL = "Save";
export const STRIP_CANCEL_LABEL = "Cancel";
export const STRIP_ASSIGN_EMPTY = "Nobody has joined this pool yet.";

// The strip options sheet (ISSUE-1290).
export function stripOptionsTitle(stripLabel: string): string {
  return `${stripLabel} options`;
}
export const STRIP_LOCK_LABEL = "Lock strip";
export const STRIP_LOCK_HINT =
  "Draws a digit from 0 to 9 for every row. Members stay on their rows.";
export const STRIP_UNLOCK_LABEL = "Unlock strip";
export const STRIP_UNLOCK_HINT =
  "Erases the digits and the winners. Members stay on their rows.";
export const STRIP_COPY_USERS_LABEL = "Copy members from a strip";
export const STRIP_COPY_USERS_HINT =
  "Puts each row's member from another strip on the same row here.";
export const STRIP_COPY_USERS_DISABLED_LOCKED =
  "Unlock this strip to copy members onto it.";
export const STRIP_COPY_USERS_DISABLED_ONE =
  "Add another strip to copy its members.";
export const STRIP_COPY_USERS_DISABLED_CANCELED =
  "This game won't be played, so the card can't change.";
export const STRIP_COPY_FROM_LABEL = "Copy members from";
export const STRIP_COPY_CONFIRM_LABEL = "Copy members";
export function stripCopyFromItemLabel(stripLabel: string): string {
  return `Members on ${stripLabel}`;
}
export const STRIP_CLOSE_LABEL = "Close";
export function stripUnlockConfirmTitle(stripLabel: string): string {
  return `Unlock ${stripLabel}?`;
}
export function stripUnlockConfirmBody(stripLabel: string): string {
  return `This erases every digit and every winner on ${stripLabel}. Members keep their rows.`;
}
export const STRIP_UNLOCK_CONFIRM_LABEL = "Unlock strip";
export function stripLockConfirmTitle(stripLabel: string): string {
  return `Lock ${stripLabel}?`;
}
export const STRIP_LOCK_CONFIRM_BODY =
  "Every row gets a digit from 0 to 9, drawn at random. Rows can't change while the strip is locked.";

// Winners (ISSUE-1291).
export const STRIP_WINNER_SHEET_TITLE = "Winning spot";
export function stripWinnerSheetSubtitle(input: {
  stripLabel: string;
  rowLabel: string;
  holder: string | null;
  digit: number | null;
}): string {
  const who = input.holder ?? "Open row";
  const digit = input.digit === null ? "" : `, digit ${String(input.digit)}`;
  return `${who}, ${input.stripLabel} row ${input.rowLabel}${digit}`;
}
export const STRIP_WINNER_EMPTY = "No winners on this row yet.";
/**
 * QA 2026-11-02. An admin may mark a winning digit nobody holds (the design's
 * "an open row can carry a winner"); the sheet says what that means so the
 * Results table's "Open spot" is no surprise.
 */
export const STRIP_WINNER_OPEN_ROW_NOTE =
  "Nobody holds this row, so a win here goes to no one. Results lists it as an open spot.";
export const STRIP_WINNER_ADD_LABEL = "Add a winner";
export const STRIP_WINNER_REMOVE_LABEL = "Remove";
export const STRIP_WINNER_DATE_LABEL = "Date";
export const STRIP_WINNER_NOTE_LABEL = "Note";
export const STRIP_WINNER_NOTE_HINT = "For example, end of the first quarter.";
export const STRIP_WINNER_NOTE_REQUIRED = "Add a short note for this winner.";
export const STRIP_WINNER_DATE_REQUIRED = "Pick the date this row won.";
export const STRIP_WINNER_SAVE_LABEL = "Save winners";
export const STRIP_WINNER_MAX_REACHED = "A row can have up to 10 winners.";
export function stripWinnerItemHeading(index: number): string {
  return `Winner ${String(index + 1)}`;
}
export const STRIP_DISCARD_TITLE = "Discard your changes?";
export const STRIP_DISCARD_BODY = "The winners you edited won't be saved.";
export const STRIP_DISCARD_LABEL = "Discard";

// Add and remove a game (ISSUE-1292).
export const STRIP_ADD_GAME_TITLE = "Add a game";
export const STRIP_ADD_GAME_HINT =
  "The new game gets its own card, starting with S1.";
export const STRIP_ADD_GAME_CONFIRM_LABEL = "Add game";
export const STRIP_ADD_GAME_TAKEN_HINT = "Already in this pool.";
export function stripRemoveGameConfirmTitle(gameLabel: string): string {
  return `Remove ${gameLabel}?`;
}
export const STRIP_REMOVE_GAME_CONFIRM_BODY =
  "This removes the game's card, every strip on it and its winners.";
export const STRIP_REMOVE_GAME_CONFIRM_LABEL = "Remove game";

// Custom games with no feed (ISSUE-1299).
export const STRIP_ADD_GAME_SOURCE_LABEL = "Kind of game";
export const STRIP_ADD_GAME_SOURCE_SCHEDULE = "On the schedule";
export const STRIP_ADD_GAME_SOURCE_CUSTOM = "Custom game";
export const STRIP_CUSTOM_GAME_HINT =
  "For a game we don't carry. You name it, and an admin picks the winners by hand.";
export const STRIP_CUSTOM_GAME_NAME_LABEL = "Game name";
export const STRIP_CUSTOM_GAME_NAME_HINT =
  "Leave it blank and we'll call it Custom game with a number.";
export const STRIP_SETUP_CUSTOM_GAME_NAME_HINT =
  "Leave it blank and we'll call it Custom game 1.";
export const STRIP_CUSTOM_GAME_NAME_PLACEHOLDER = "Office Bowl";
export const STRIP_CUSTOM_GAME_NAME_MAX_LENGTH = 40;
export const STRIP_CUSTOM_GAME_TAG = "Custom game";
export const STRIP_CUSTOM_GAME_WINNERS_NOTE =
  "No live score for this one. An admin picks the winners by hand.";
export const STRIP_RENAME_GAME_LABEL = "Rename game";
export const STRIP_RENAME_GAME_TITLE = "Rename this game";
export const STRIP_RENAME_GAME_SAVE_LABEL = "Save";
export const STRIP_RENAME_GAME_EMPTY_ERROR = "Give the game a name.";
export const STRIP_DISCARD_GAME_NAME_BODY =
  "The game name you typed won't be saved.";

// The game header and box score under the board (ISSUE-1298).
export function stripGameHeading(input: {
  awayTeamName: string;
  homeTeamName: string;
}): string {
  return `${input.awayTeamName} at ${input.homeTeamName}`;
}
export const STRIP_BOX_SCORE_UNAVAILABLE =
  "The score shows up here once the game starts.";
export const STRIP_FULL_GAME_LABEL = "Open game details";

// Results (ISSUE-1293).
export const STRIP_RESULTS_HEADING = "Winners";
export const STRIP_RESULTS_MEMBERS_LABEL = "Members";
export const STRIP_RESULTS_ALL_MEMBERS = "All members";
export function stripResultsMembersSummary(count: number): string {
  return count === 1 ? "1 member" : `${String(count)} members`;
}
export const STRIP_RESULTS_STRIP_LABEL = "Strip";
export const STRIP_RESULTS_ALL_STRIPS = "All strips";
export const STRIP_RESULTS_GAME_LABEL = "Game";
export const STRIP_RESULTS_ALL_GAMES = "All games";
export const STRIP_RESULTS_EMPTY = "No winners yet.";
export const STRIP_RESULTS_EMPTY_FILTERED = "No winners match these filters.";
export const STRIP_RESULTS_LOAD_FAILED =
  "We couldn't load the winners. Try again in a moment.";
export const STRIP_RESULTS_COLUMN_MEMBER = "Member";
export const STRIP_RESULTS_COLUMN_STRIP = "Strip";
export const STRIP_RESULTS_COLUMN_SPOT = "Spot";
export const STRIP_RESULTS_COLUMN_NOTES = "Notes";
export const STRIP_RESULTS_COLUMN_EDIT = "Edit";
export const STRIP_RESULTS_EDIT_LABEL = "Edit";
export function stripResultsEditAriaLabel(displayName: string): string {
  return `Edit winner ${displayName}`;
}
export const STRIP_RESULTS_PICK_MEMBERS_TITLE = "Show winners for";
export const STRIP_RESULTS_APPLY_LABEL = "Show winners";
export const STRIP_RESULTS_CLEAR_LABEL = "All members";
export const STRIP_WINNER_EDIT_TITLE = "Edit winner";
export const STRIP_WINNER_EDIT_SAVE_LABEL = "Save";

// Settings (ISSUE-1294).
export const STRIP_SETTINGS_HEADING = "Strip Card";
export const STRIP_SETTINGS_FIRST_GAME_LABEL = "First game";
export const STRIP_SETTINGS_SAVE_LABEL = "Save";
export const STRIP_SETTINGS_SAVED = "Saved";
export const STRIP_SETTINGS_LOAD_FAILED =
  "We couldn't load the card settings. Try again in a moment.";
export const STRIP_RULES_HEADING = "How Strip Card works";
export const STRIP_FIRST_GAME_GONE = "Removed from the pool";
